import styled from "@emotion/styled";
import { motion } from "framer-motion";

// Styled Components
const AboutContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: ${(props) => props.theme.spacing.xl}
    ${(props) => props.theme.spacing.lg};
`;

const AboutTitle = styled.h1`
  font-size: 3rem;
  font-weight: 800;
  color: ${(props) => props.theme.colors.text};
  text-align: center;
  margin-bottom: ${(props) => props.theme.spacing.lg};
`;

const AboutContent = styled(motion.div)`
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border-radius: ${(props) => props.theme.borderRadius.lg};
  padding: ${(props) => props.theme.spacing.lg};
  border: 1px solid rgba(255, 255, 255, 0.2);
  margin-bottom: ${(props) => props.theme.spacing.lg};
`;

const AboutText = styled.p`
  color: ${(props) => props.theme.colors.textLight};
  line-height: 1.8;
  font-size: 1.1rem;
  margin-bottom: ${(props) => props.theme.spacing.md};
`;

const TeamSection = styled.section`
  margin-top: ${(props) => props.theme.spacing.xl};
`;

const TeamTitle = styled.h2`
  color: ${(props) => props.theme.colors.text};
  font-size: 2rem;
  margin-bottom: ${(props) => props.theme.spacing.lg};
  text-align: center;
`;

const TeamGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: ${(props) => props.theme.spacing.lg};
`;

const TeamMember = styled(motion.div)`
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border-radius: ${(props) => props.theme.borderRadius.md};
  padding: ${(props) => props.theme.spacing.lg};
  border: 1px solid rgba(255, 255, 255, 0.2);
  text-align: center;
  transition: transform 0.3s ease;

  &:hover {
    transform: translateY(-5px);
  }
`;

const MemberName = styled.h3`
  color: ${(props) => props.theme.colors.text};
  font-size: 1.25rem;
  margin-bottom: ${(props) => props.theme.spacing.xs};
`;

const MemberRole = styled.p`
  color: ${(props) => props.theme.colors.textLight};
  font-size: 0.9rem;
  margin-bottom: ${(props) => props.theme.spacing.sm};
`;

const MemberBio = styled.p`
  color: ${(props) => props.theme.colors.textLight};
  font-size: 0.9rem;
  line-height: 1.6;
`;

// Animation variants for content only

const contentVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
};

const memberVariants = {
  hidden: { opacity: 0, scale: 0.9 },
  visible: { opacity: 1, scale: 1 },
};

function About() {
  return (
    <AboutContainer>
      <AboutTitle>About TelazerCom</AboutTitle>

      <AboutContent
        variants={contentVariants}
        initial="hidden"
        animate="visible"
        transition={{ delay: 0.1 }}
      >
        <AboutText>
          TelazerCom is a cutting-edge React application that showcases modern
          web development practices. Built with TypeScript, Emotion.js, and
          React Router, it demonstrates how to create beautiful, performant, and
          maintainable web applications.
        </AboutText>
        <AboutText>
          Our mission is to provide developers with a solid foundation for
          building scalable React applications. We focus on clean code
          architecture, smooth user experiences, and modern development
          workflows.
        </AboutText>
        <AboutText>
          The application features smooth page transitions, responsive design,
          and a comprehensive component library that can be easily extended and
          customized for any project needs.
        </AboutText>
      </AboutContent>

      <TeamSection>
        <TeamTitle>Our Team</TeamTitle>
        <TeamGrid>
          <TeamMember
            variants={memberVariants}
            initial="hidden"
            animate="visible"
            transition={{ delay: 0.2 }}
          >
            <MemberName>John Doe</MemberName>
            <MemberRole>Lead Developer</MemberRole>
            <MemberBio>
              Passionate about React and modern web technologies. Loves creating
              smooth user experiences and clean, maintainable code.
            </MemberBio>
          </TeamMember>

          <TeamMember
            variants={memberVariants}
            initial="hidden"
            animate="visible"
            transition={{ delay: 0.3 }}
          >
            <MemberName>Jane Smith</MemberName>
            <MemberRole>UI/UX Designer</MemberRole>
            <MemberBio>
              Creative designer focused on creating beautiful and intuitive user
              interfaces that enhance user experience.
            </MemberBio>
          </TeamMember>

          <TeamMember
            variants={memberVariants}
            initial="hidden"
            animate="visible"
            transition={{ delay: 0.4 }}
          >
            <MemberName>Mike Johnson</MemberName>
            <MemberRole>Backend Developer</MemberRole>
            <MemberBio>
              Expert in building robust backend systems and APIs that power
              modern web applications.
            </MemberBio>
          </TeamMember>
        </TeamGrid>
      </TeamSection>
    </AboutContainer>
  );
}

export default About;
